import type { ParsedUrlQuery } from 'querystring';
import {
  ALERTS_SHORT_NAME,
  DASHBOARD_SHORT_NAME,
  DisplayedSubscriptionNames,
  IGNORED_PRICE_IDS,
  SubscriptionType,
} from 'constants/payments';
import { SETTINGS_PAGE } from 'constants/pages';
import { SettingsApiResponse } from './props';

/**
 * A link support can send that opens the cancellation flow for one product:
 *
 *   /my/settings?cancel=dashboard
 *   /my/settings?cancel=alerts
 *
 * It opens the flow and nothing more. Every question in it still applies, and the session it
 * creates is checked against the signed-in account on the server, so a forwarded link opens the
 * flow on the forwarder's own subscription and nobody else's. When the product is not on the
 * account, is already cancelled, or has no billing to cancel, the link lands on plain Settings.
 */
export const CANCEL_QUERY_PARAM = 'cancel';

const PRODUCT_BY_SHORT_NAME: Record<string, SubscriptionType> = {
  [DASHBOARD_SHORT_NAME]: SubscriptionType.DashboardStandard,
  [ALERTS_SHORT_NAME]: SubscriptionType.SmartMoneyAlerts,
};

/** The product a support link names (`dashboard` | `alerts`, any case), or null. */
export const getProductTypeForShortName = (requested: unknown): SubscriptionType | null =>
  typeof requested === 'string' ? PRODUCT_BY_SHORT_NAME[requested.toLowerCase()] || null : null;

export const getSupportCancelLink = (product: 'dashboard' | 'alerts') =>
  `${SETTINGS_PAGE}?${CANCEL_QUERY_PARAM}=${product}`;

export interface CancelDeepLinkTarget {
  id: string;
  displayName: string;
  externalSubscriptionId: string;
}

type SettingsSubscription = SettingsApiResponse['subscriptions'][number];
type SettingsForDeepLink = Pick<SettingsApiResponse, 'subscriptions' | 'stripeSubscriptions'>;

/**
 * The same rule the subscription row uses to show its Cancel link. Lifetime is a property of the
 * Stripe record, never of the user; a comped plan is billed at nothing so there is no cancellation
 * to make; an overdue plan can still be closed even though its renewal is already off.
 */
export const isCancellableSubscription = (
  subscription: SettingsSubscription,
  settings: SettingsForDeepLink,
) => {
  if (subscription.endedAt) return false;
  const isLifetime = (settings.stripeSubscriptions || []).some(
    (stripeSubscription) =>
      stripeSubscription.isLifetime &&
      stripeSubscription.subscriptionId === subscription.externalSubscriptionId,
  );
  if (isLifetime) return false;
  if (IGNORED_PRICE_IDS.includes(subscription.priceId || '')) return false;
  const isPastDue = ['past_due', 'unpaid'].includes(subscription.status);
  return !subscription.cancelAtPeriodEnd || isPastDue;
};

/** The subscription a `?cancel=` link names, when there is one worth opening the flow for. */
export const getCancelDeepLinkTarget = (
  query: ParsedUrlQuery,
  settings: SettingsForDeepLink,
): CancelDeepLinkTarget | null => {
  const type = getProductTypeForShortName(query[CANCEL_QUERY_PARAM]);
  if (!type) return null;
  const subscription = settings.subscriptions.find(
    (candidate) => candidate.type === type && isCancellableSubscription(candidate, settings),
  );
  if (!subscription) return null;
  return {
    id: subscription.id,
    displayName: DisplayedSubscriptionNames[type],
    externalSubscriptionId: subscription.externalSubscriptionId,
  };
};

export const hasCancelQueryParam = (query: ParsedUrlQuery) => CANCEL_QUERY_PARAM in query;

/** The same query without one parameter, so a refresh does not replay what it opened. */
export const withoutQueryParam = (query: ParsedUrlQuery, param: string): ParsedUrlQuery => {
  const { [param]: _consumed, ...rest } = query;
  return rest;
};

/** The same query without the `cancel` parameter, so a refresh does not reopen the flow. */
export const withoutCancelQueryParam = (query: ParsedUrlQuery): ParsedUrlQuery =>
  withoutQueryParam(query, CANCEL_QUERY_PARAM);
